import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AdminLayout } from '../components/layout/AdminLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useConfig, useUpdateConfig } from '../hooks/useConfig';
import { Save, Loader2 } from 'lucide-react';

const configSchema = z.object({
  siteName: z.string().min(1, 'Nom du site requis'),
  practitioner: z.string().nullable().optional(),
  siteTagline: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  email: z.string().email('Email invalide'),
  phone: z.string().min(1, 'Téléphone requis'),
  addressStreet: z.string().nullable().optional(),
  addressCity: z.string().nullable().optional(),
  addressZip: z.string().nullable().optional(),
  addressCountry: z.string().nullable().optional(),
  hoursWeekdays: z.string().nullable().optional(),
  hoursSaturday: z.string().nullable().optional(),
  hoursSunday: z.string().nullable().optional(),
  facebookUrl: z.string().url('URL invalide').nullable().optional().or(z.literal('')),
  instagramUrl: z.string().url('URL invalide').nullable().optional().or(z.literal('')),
  planityUrl: z.string().url('URL invalide').nullable().optional().or(z.literal('')),
  mapUrl: z.string().url('URL invalide').nullable().optional().or(z.literal('')),
});

type ConfigFormData = z.infer<typeof configSchema>;

export default function Settings() {
  const { data: config, isLoading } = useConfig();
  const updateConfig = useUpdateConfig();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<ConfigFormData>({
    resolver: zodResolver(configSchema),
  });

  useEffect(() => {
    if (config) {
      reset({
        siteName: config.siteName,
        practitioner: config.practitioner || '',
        siteTagline: config.siteTagline || '',
        description: config.description || '',
        email: config.email,
        phone: config.phone,
        addressStreet: config.addressStreet || '',
        addressCity: config.addressCity || '',
        addressZip: config.addressZip || '',
        addressCountry: config.addressCountry || '',
        hoursWeekdays: config.hoursWeekdays || '',
        hoursSaturday: config.hoursSaturday || '',
        hoursSunday: config.hoursSunday || '',
        facebookUrl: config.facebookUrl || '',
        instagramUrl: config.instagramUrl || '',
        planityUrl: config.planityUrl || '',
        mapUrl: config.mapUrl || '',
      });
    }
  }, [config, reset]);

  const onSubmit = async (data: ConfigFormData) => {
    // Convert empty strings to null
    const cleanedData = Object.fromEntries(
      Object.entries(data).map(([key, value]) => [
        key,
        value === '' ? null : value,
      ])
    );
    await updateConfig.mutateAsync(cleanedData);
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Paramètres du Site</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">
            Gérez les informations générales du site
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          {/* Informations générales */}
          <div className="bg-white p-4 sm:p-6 rounded-lg border space-y-4">
            <h2 className="text-xl font-semibold mb-4">Informations générales</h2>

            <div>
              <Label htmlFor="siteName">Nom du site *</Label>
              <Input
                id="siteName"
                {...register('siteName')}
                placeholder="Univers Massages"
              />
              {errors.siteName && (
                <p className="text-sm text-red-600 mt-1">{errors.siteName.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="practitioner">Praticienne</Label>
              <Input
                id="practitioner"
                {...register('practitioner')}
                placeholder="Virginie Bergeret"
              />
            </div>

            <div>
              <Label htmlFor="siteTagline">Slogan</Label>
              <Input
                id="siteTagline"
                {...register('siteTagline')}
                placeholder="Au-delà du bien-être, le mieux-être"
              />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                {...register('description')}
                placeholder="Massages bien-être et soins énergétiques..."
                rows={3}
              />
            </div>
          </div>

          {/* Contact */}
          <div className="bg-white p-4 sm:p-6 rounded-lg border space-y-4">
            <h2 className="text-xl font-semibold mb-4">Contact</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  {...register('email')}
                  placeholder="contact@univers-massage.fr"
                />
                {errors.email && (
                  <p className="text-sm text-red-600 mt-1">{errors.email.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="phone">Téléphone *</Label>
                <Input
                  id="phone"
                  {...register('phone')}
                  placeholder="06 12 34 56 78"
                />
                {errors.phone && (
                  <p className="text-sm text-red-600 mt-1">{errors.phone.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Adresse */}
          <div className="bg-white p-4 sm:p-6 rounded-lg border space-y-4">
            <h2 className="text-xl font-semibold mb-4">Adresse</h2>

            <div>
              <Label htmlFor="addressStreet">Rue</Label>
              <Input
                id="addressStreet"
                {...register('addressStreet')}
                placeholder="12 Rue de la Grotte"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="addressZip">Code postal</Label>
                <Input
                  id="addressZip"
                  {...register('addressZip')}
                  placeholder="65100"
                />
              </div>

              <div>
                <Label htmlFor="addressCity">Ville</Label>
                <Input
                  id="addressCity"
                  {...register('addressCity')}
                  placeholder="Lourdes"
                />
              </div>

              <div>
                <Label htmlFor="addressCountry">Pays</Label>
                <Input
                  id="addressCountry"
                  {...register('addressCountry')}
                  placeholder="France"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="mapUrl">URL Google Maps</Label>
              <Input
                id="mapUrl"
                {...register('mapUrl')}
                placeholder="https://maps.google.com/?q=..."
              />
              {errors.mapUrl && (
                <p className="text-sm text-red-600 mt-1">{errors.mapUrl.message}</p>
              )}
            </div>
          </div>

          {/* Horaires */}
          <div className="bg-white p-4 sm:p-6 rounded-lg border space-y-4">
            <h2 className="text-xl font-semibold mb-4">Horaires d'ouverture</h2>

            <div>
              <Label htmlFor="hoursWeekdays">Lundi - Vendredi</Label>
              <Input
                id="hoursWeekdays"
                {...register('hoursWeekdays')}
                placeholder="9h00 - 19h00"
              />
            </div>

            <div>
              <Label htmlFor="hoursSaturday">Samedi</Label>
              <Input
                id="hoursSaturday"
                {...register('hoursSaturday')}
                placeholder="9h00 - 17h00"
              />
            </div>

            <div>
              <Label htmlFor="hoursSunday">Dimanche</Label>
              <Input
                id="hoursSunday"
                {...register('hoursSunday')}
                placeholder="Fermé"
              />
            </div>
          </div>

          {/* Réseaux sociaux */}
          <div className="bg-white p-4 sm:p-6 rounded-lg border space-y-4">
            <h2 className="text-xl font-semibold mb-4">Réseaux sociaux et réservation</h2>

            <div>
              <Label htmlFor="facebookUrl">Facebook</Label>
              <Input
                id="facebookUrl"
                {...register('facebookUrl')}
                placeholder="https://facebook.com/..."
              />
              {errors.facebookUrl && (
                <p className="text-sm text-red-600 mt-1">{errors.facebookUrl.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="instagramUrl">Instagram</Label>
              <Input
                id="instagramUrl"
                {...register('instagramUrl')}
                placeholder="https://instagram.com/..."
              />
              {errors.instagramUrl && (
                <p className="text-sm text-red-600 mt-1">{errors.instagramUrl.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="planityUrl">Planity (Réservation)</Label>
              <Input
                id="planityUrl"
                {...register('planityUrl')}
                placeholder="https://www.planity.com/..."
              />
              {errors.planityUrl && (
                <p className="text-sm text-red-600 mt-1">{errors.planityUrl.message}</p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-4">
            <Button
              type="submit"
              disabled={!isDirty || updateConfig.isPending}
            >
              {updateConfig.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Enregistrer les modifications
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
}
